<template>
    <div class="req-tab">
        <div class="req-hd">
            <div>
                <h2>Requisições</h2>
                <div class="req-sub">Origem de ressuprimento de cada loja nesta cotação</div>
            </div>
            <div class="req-summary">
                <div class="summary-card">
                    <span>Requisições</span>
                    <b>{{ requisicoesCount }}</b>
                </div>
            </div>
        </div>

        <div class="req-filterbar">
            <a-select
                class="req-filter-select fixed-height-select"
                placeholder="Loja"
                v-model="storeFilter"
                mode="multiple"
                allow-clear
                :dropdown-style="{ minWidth: '300px' }"
            >
                <a-select-option v-for="option in storeOptions" :key="option.key" :value="option.key">
                    {{ option.label }}
                </a-select-option>
            </a-select>
        </div>

        <a-card :bordered="false" class="req-card" :style="selectedRows.length && { marginBottom: '60px' }">
            <a-table
                size="small"
                :columns="columns"
                :data-source="filteredRows"
                :loading="loading"
                :pagination="false"
                row-key="id"
                :row-selection="rowSelection"
                :expandedRowKeys="expanded"
                :expand-icon-column-index="2"
                :locale="{ emptyText: 'Nenhuma loja com solicitação nesta cotação.' }"
                @expand="onExpandChange"
            >
                <template slot="loja" slot-scope="text, row">
                    <div class="bl-strong">{{ row.store_name || `Loja ${row.store_id}` }}</div>
                </template>

                <template slot="requisicao" slot-scope="text, row">
                    <template v-if="row.resupply">
                        <div class="bl-n">Requisição #{{ row.resupply.id }}<template v-if="row.resupply.name"> — {{ row.resupply.name }}</template></div>
                    </template>
                    <span v-else class="req-empty">Sem origem rastreada</span>
                </template>

                <template slot="origem" slot-scope="text, row">
                    {{ row.resupply ? (row.resupply.origin || "—") : "—" }}
                </template>

                <template slot="criacao" slot-scope="text, row">
                    {{ row.resupply ? formatDate(row.resupply.created_at) : "—" }}
                </template>

                <template slot="produtos" slot-scope="text, row">
                    {{ row.products_count != null ? row.products_count : "—" }}
                </template>

                <template slot="status" slot-scope="text, row">
                    {{ row.resupply ? (row.resupply.status || "—") : "—" }}
                </template>

                <template slot="criador" slot-scope="text, row">
                    {{ row.resupply ? (row.resupply.creator || "—") : "—" }}
                </template>

                <template slot="expandedRowRender" slot-scope="row">
                    <div class="expanded-table-wrapper">
                        <span v-if="!row.resupply" class="req-empty">Sem origem rastreada — não há produtos para exibir.</span>

                        <cc-loader-full v-else-if="loadingProducts[row.id]" />

                        <a-table
                            v-else
                            size="small"
                            :columns="productColumns"
                            :data-source="productsByRow[row.id] || []"
                            :pagination="false"
                            row-key="pro_id"
                            :locale="{ emptyText: 'Nenhum produto encontrado nesta requisição.' }"
                        >
                            <template slot="produto" slot-scope="text, product">
                                <div class="pro-name">{{ product.pro_descricao }}</div>
                                <div class="pro-ean" v-if="product.pro_ean || product.pro_codInterno">
                                    <template v-if="product.pro_ean">EAN {{ product.pro_ean }}</template>
                                    <template v-if="product.pro_ean && product.pro_codInterno"> · </template>
                                    <template v-if="product.pro_codInterno">Cód. {{ product.pro_codInterno }}</template>
                                </div>
                            </template>
                            <template slot="ressuprimento" slot-scope="text, product">
                                <span class="repl-tag" v-if="replenishment(product)">{{ replenishment(product) }}</span>
                                <span v-else>--</span>
                            </template>
                            <template slot="status" slot-scope="text, product">
                                <span v-if="product.purchase_status === 'NO_OFFER'" class="req-empty">Sem oferta</span>
                                <span v-else class="status-tag" :class="{ 'status-tag-purchased': product.purchase_status === 'ORDERED' }">
                                    {{ product.purchase_status === 'ORDERED' ? "Comprado" : "Não comprado" }}
                                </span>
                            </template>
                            <template slot="embalagem" slot-scope="text, product">{{ product.pro_embalagem || "—" }}</template>
                            <template slot="estoque" slot-scope="text, product">{{ product.estoque != null ? $options.filters.stock(product.estoque) : "—" }}</template>
                            <template slot="pedido" slot-scope="text, product">{{ product.quantidade }}</template>
                            <template slot="ofertas" slot-scope="text, product">{{ product.offers_count || 0 }}</template>
                            <template slot="menorPreco" slot-scope="text, product">
                                <span v-if="product.min_offer_price != null">{{ product.min_offer_price | currency }}</span>
                                <span v-else>—</span>
                            </template>
                            <template slot="ultimoPreco" slot-scope="text, product">
                                <span v-if="product.last_price != null">{{ product.last_price | currency }}</span>
                                <span v-else>—</span>
                            </template>
                            <template slot="observacao" slot-scope="text, product">{{ product.observacao || "—" }}</template>
                        </a-table>
                    </div>
                </template>
            </a-table>
        </a-card>

        <SelectionFooter
            :count="selectedRows.length"
            item-label="requisições selecionadas"
        >
            <template slot="actions">
                <a-button size="large" @click="selectedRowKeys = []">Limpar seleção</a-button>
                <a-button size="large" type="primary" :loading="replicating" @click="bulkReplicate">Replicar selecionadas</a-button>
            </template>
        </SelectionFooter>
    </div>
</template>

<script>
import RequestService from "@/services/v3/request.service";
import RequestProductService from "@/services/v3/requests/request-products.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { replenishmentLabel } from "@/helpers/replenishment";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";

export default {
    name: "RequisicoesTab",
    components: { SelectionFooter },
    data() {
        return {
            v3Svc: new RequestService(),
            productsSvc: new RequestProductService(),
            loading: false,
            rows: [],
            storeFilter: [],
            expanded: [],
            productsByRow: {},
            loadingProducts: {},
            selectedRowKeys: [],
            replicating: false,
        };
    },
    computed: {
        requisicoesCount() {
            const ids = new Set(
                this.rows.filter((row) => row.resupply).map((row) => row.resupply.id)
            );
            return ids.size;
        },
        storeOptions() {
            const seen = new Set();
            const options = [];

            this.rows.forEach((row) => {
                if (seen.has(row.store_id)) return;

                seen.add(row.store_id);
                options.push({
                    key: row.store_id,
                    label: row.store_name || `Loja ${row.store_id}`,
                    number: row.store_number,
                });
            });

            return options.sort((a, b) => Number(a.number) - Number(b.number));
        },
        filteredRows() {
            if (!this.storeFilter.length) return this.rows;

            return this.rows.filter((row) => this.storeFilter.includes(row.store_id));
        },
        selectedRows() {
            const keys = new Set(this.selectedRowKeys);
            return this.rows.filter((row) => keys.has(row.id));
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedRowKeys,
                onChange: (selectedRowKeys) => {
                    this.selectedRowKeys = selectedRowKeys;
                },
            };
        },
        columns() {
            return [
                { title: "Loja", key: "loja", scopedSlots: { customRender: "loja" } },
                { title: "Requisição de origem", key: "requisicao", scopedSlots: { customRender: "requisicao" } },
                { title: "Origem", key: "origem", scopedSlots: { customRender: "origem" } },
                { title: "Criada em", key: "criacao", scopedSlots: { customRender: "criacao" } },
                { title: "Qtd. produtos", key: "produtos", align: "center", scopedSlots: { customRender: "produtos" } },
                { title: "Status da requisição", key: "status", scopedSlots: { customRender: "status" } },
                { title: "Criada por", key: "criador", scopedSlots: { customRender: "criador" } },
            ];
        },
        productColumns() {
            return [
                { title: "Produto", key: "produto", scopedSlots: { customRender: "produto" } },
                { title: "Reabastecimento", key: "ressuprimento", align: "center", scopedSlots: { customRender: "ressuprimento" } },
                { title: "Status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Qtd. Emb.", key: "embalagem", align: "center", scopedSlots: { customRender: "embalagem" } },
                { title: "Estoque", key: "estoque", align: "center", scopedSlots: { customRender: "estoque" } },
                { title: "Pedido", key: "pedido", align: "center", scopedSlots: { customRender: "pedido" } },
                { title: "Qtd. Ofertas", key: "ofertas", align: "center", scopedSlots: { customRender: "ofertas" } },
                { title: "Menor Preço", key: "menorPreco", align: "right", scopedSlots: { customRender: "menorPreco" } },
                { title: "Último Preço", key: "ultimoPreco", align: "right", scopedSlots: { customRender: "ultimoPreco" } },
                { title: "Observação", key: "observacao", scopedSlots: { customRender: "observacao" } },
            ];
        },
    },
    created() {
        this.load();
    },
    methods: {
        formatDate(date) {
            if (!date) return "—";
            return this.$options.filters.formatDateOnly(date);
        },
        replenishment(product) {
            return replenishmentLabel(product.replenishment_strategy);
        },
        load() {
            this.loading = true;

            this.v3Svc
                .resupply_origins(this.$route.params.id)
                .then((response) => {
                    this.rows = response.data || [];
                    this.$emit("count-changed", this.rows.length);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onExpandChange(isExpanded, row) {
            const idx = this.expanded.indexOf(row.id);
            if (isExpanded && idx === -1) this.expanded.push(row.id);
            else if (!isExpanded && idx !== -1) this.expanded.splice(idx, 1);

            if (isExpanded && row.resupply) this.loadRowProducts(row);
        },
        bulkReplicate() {
            if (!this.selectedRows.length) return;

            this.$swal
                .fire({
                    title: `Replicar ${this.selectedRows.length} requisição(ões) selecionada(s)?`,
                    text: "Cada uma vira uma nova solicitação enviada para a central de compras, com os mesmos produtos e quantidades.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Sim, replicar",
                    cancelButtonText: "Cancelar",
                })
                .then((result) => {
                    if (!result.value) return;

                    const subrequestIds = Array.from(
                        new Set(this.selectedRows.map((row) => row.solicitacao_id))
                    );

                    this.replicating = true;

                    this.v3Svc
                        .replicate(this.$route.params.id, subrequestIds)
                        .then((response) => {
                            const total = (response.data.created_request_ids || []).length;

                            this.$swal.fire(
                                "Feito!",
                                total
                                    ? `${total} nova(s) requisição(ões) criada(s) no monitor de ressuprimento.`
                                    : "Nenhuma das requisições selecionadas tinha produtos para replicar.",
                                total ? "success" : "warning"
                            );

                            this.selectedRowKeys = [];
                        })
                        .catch((error) => {
                            ErrorHandlerService.handle(error, this.$store);
                        })
                        .finally(() => {
                            this.replicating = false;
                        });
                });
        },
        loadRowProducts(row) {
            if (this.productsByRow[row.id]) return;

            this.$set(this.loadingProducts, row.id, true);

            this.productsSvc
                .resupply_products(row.resupply.id)
                .then((response) => {
                    this.$set(this.productsByRow, row.id, (response.data && response.data.produtos) || []);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.$set(this.loadingProducts, row.id, false);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.req-tab {
    padding: 20px;
}
.req-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.req-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.req-summary {
    display: flex;
    gap: 12px;
}
.req-filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}
.req-filter-select {
    width: 220px;
}
.fixed-height-select.ant-select,
.fixed-height-select ::v-deep(.ant-select-selection--multiple) {
    min-height: 32px !important;
    max-height: 32px !important;
    overflow: auto;
}
.summary-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    span {
        color: #8c8c8c;
        font-size: 13px;
    }

    b {
        font-size: 18px;
        color: #262626;
    }
}
.req-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
}
.bl-strong {
    font-weight: 600;
    color: #262626;
}
.bl-n {
    font-weight: 600;
    color: #262626;
}
.req-empty {
    color: #bfbfbf;
    font-style: italic;
}
.expanded-table-wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
}
.pro-name {
    font-weight: 600;
    color: #262626;
}
.pro-ean {
    font-size: 11px;
    color: #8c8c8c;
}
.repl-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.status-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #fff7e6;
    color: #d46b08;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.status-tag-purchased {
    background: #f6ffed;
    color: #389e0d;
}
</style>
